import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

interface IAuthContext {
  isLoggedIn: boolean;
  setIsLoggedIn: (is: boolean) => void;
  checkAuthStatus: () => Promise<boolean>;
}

export const AuthContext = createContext<IAuthContext | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthContextProvider: React.FunctionComponent<
  AuthProviderProps
> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const location = useLocation();
  const currentPath = location.pathname;

  // Check if we're on an admin route
  const isAdminRoute =
    currentPath.startsWith("/admin") || currentPath === "/prijava";

  // Function to check authentication status
  const checkAuthStatus = async (): Promise<boolean> => {
    try {
      const response = await fetch(
        process.env.REACT_APP_BACKEND_URL + "login",
        {
          credentials: "include",
        }
      );

      const authenticated = response.status === 200;
      setIsLoggedIn(authenticated);
      return authenticated;
    } catch (error) {
      console.error("Auth check failed:", error);
      setIsLoggedIn(false);
      return false;
    }
  };

  useEffect(() => {
    // Only check auth status on admin routes
    if (isAdminRoute) {
      checkAuthStatus();
    }
  }, [isAdminRoute]);

  return (
    <AuthContext.Provider
      value={{ isLoggedIn, setIsLoggedIn, checkAuthStatus }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      "AuthContext should be used within the AuthContextProvider"
    );
  }

  return context;
};
